import { useCallback, useEffect, useState, useRef } from "react";
import Multiselect from "multiselect-react-dropdown";
import { uniqBy, sortBy } from "lodash";
import { useUserProvider } from "../providers/UserProvider";
import { Select } from "../components/EntryForm";
import { GetAppResource, GetAllApps } from "../services/api/app";
import { isEmpty } from "lodash";
import { Work } from "@material-ui/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import { IoIosCloseCircle } from "react-icons/io";
import { ReactComponent as PencilIcon } from "../assets/icons/pencil.svg";

const DataSelector = (props) => {
  const {
    name,
    label,
    labelClassName,
    placeholder = null,
    formik,
    resourceEndpoint,
    endpoint = "resource",
    dataSet,
    single,
    singleSelectOnly,
    keyOption,
    filteredOptions,
    inSubtable,
    onEditClick,
    withSearch,
    filterOptions,
    noMarginBottom = false,
    sortByName = false,
    setCurrentData,
    setCurrentSelected,
    setIsDataLoaded,
    onChange,
    disabled,
    withLegend,
    type,
    makeNullOnRemove = false,
    required = false,
    enableDragDrop = false,
    maximumSelection,
    valueKey = "id",
    labelKey
  } = props;
  let value = formik.values[name];

  const [data, setData] = useState({
    options: [],
    value: [],
    loading: false,
  });
  
  const [orderedSelection, setOrderedSelection] = useState([]);
  
  const skipNextFetch = useRef(false);

  // Get values for dot separated fieldnames (ex. profile.name)
  if (name?.indexOf(".") > -1) {
    const paths = name.split(".");
    value = formik?.values?.[paths[0]]?.[paths[1]];
  }

  // Handle drag end event
  const onDragEnd = (result) => {
    // Dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = Array.from(orderedSelection);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedSelection(items);
    
    // Set flag to skip next API fetch since this is a drag operation
    skipNextFetch.current = true;
    
    // Update formik values with the new order
    if (single) {
      formik.setFieldValue(name, items.length ? items[0] : {});
    } else {
      formik.setFieldValue(name, items);
    }
    
    if (typeof setCurrentSelected === "function") {
      setCurrentSelected(items);
    }
  };

  const selectHandler = (selectedList, selectedItem) => {
    const values = selectedList.map((item) => ({
      id: item.id,
      name: item?.name,
      ...item,
    }));

    // Add the new item to our ordered list
    setOrderedSelection(values);
    if (typeof setCurrentSelected === "function") setCurrentSelected(values);

    // Set flag to skip next API fetch since this is a selection operation
    skipNextFetch.current = true;

    if (single) {
      formik.setFieldValue(name, values.length ? selectedItem : {});
    } else {
      formik.setFieldValue(name, values);
    }
  };

  const removeHandler = (selectedList, removedItem) => {
    const values = selectedList.map((item) => ({
      ...item,
    }));

    // Update our ordered list by removing the item
    setOrderedSelection(values);

    if (typeof setCurrentSelected === "function") setCurrentSelected(values);

    // Set flag to skip next API fetch since this is a removal operation
    skipNextFetch.current = true;

    if (single) {
      formik.setFieldValue(name, values.length ? values[0] : makeNullOnRemove ? null : {});
    } else {
      formik.setFieldValue(name, values);
    }
  };

  // Custom removal function for draggable items
  const removeCustomItem = (itemToRemove) => {
    const newSelection = orderedSelection.filter(
      (item) => item.id !== itemToRemove.id
    );
    setOrderedSelection(newSelection);

    if (typeof setCurrentSelected === "function") {
      setCurrentSelected(newSelection);
    }

    // Set flag to skip next API fetch since this is a removal operation
    skipNextFetch.current = true;

    if (single) {
      formik.setFieldValue(name, newSelection.length ? newSelection[0] : makeNullOnRemove ? null : {});
    } else {
      formik.setFieldValue(name, newSelection);
    }
  };

  const { user } = useUserProvider();

  const setParentStates = useCallback(
    (jsonData) => {
      if (typeof setCurrentData === "function") {
        setCurrentData(jsonData);
        if (typeof setIsDataLoaded === "function") setIsDataLoaded(true);
      }
    },
    [setCurrentData, setIsDataLoaded]
  );

  const fetchData = useCallback((endpoint) => {
    setData({ ...data, loading: true });
    const fetchDataCallback = (res) => res.json().then((jsonData) => {
        let allOptions = [];

        if (filterOptions) {
          allOptions = filterOptions(jsonData.data).map((datum) => ({
            name: datum.name ?? datum.title,
            id: datum.id,
            isEnterprise: datum?.enterprise,
          }));
        } else {
          allOptions = jsonData.data.map((datum) => ({
            name: datum.name ?? datum.title,
            id: datum.id,
            isEnterprise: datum?.enterprise,
          }));
        }

        if (keyOption) {
          allOptions = allOptions.map((option) => {
            const match = jsonData.data.find((datum) => {
              return (
                (datum.id && datum.id === option.id) ||
                (datum.name && datum.name === option.name)
              );
            });
        
            return {
              ...option,
              id: match?.[keyOption] ?? option.id,
            };
          });
        }

        if (filteredOptions?.length && formik.values[name] === "") {
          const selectedIds = filteredOptions?.map((f) =>
            f[name]?.toLowerCase()
          );
          allOptions = allOptions?.filter((datum) => {
            if (selectedIds?.includes(datum?.id?.toLowerCase())) return;
            return datum;
          });
        }

        allOptions = sortByName
          ? sortBy(uniqBy(allOptions, "name"), "name")
          : sortBy(uniqBy(allOptions, "id"), "id");

        const currentValue = value && (() => {
          const formikValues = Array.isArray(value) ? value : [value];
          return formikValues
            .map((val) => {
              if (!val || !val.id) return null;
              return allOptions?.find((opt) => opt.id === val.id);
            })
            .filter(Boolean); 
        })();

        setData({
          loading: false,
          options: allOptions,
          value: currentValue,
        });

        if (currentValue) {
          setOrderedSelection(currentValue);
        }

        if (typeof setCurrentSelected === "function" && currentValue)
          setCurrentSelected(currentValue);

        setParentStates(jsonData);
      });

    if (value && inSubtable) {
      const v = data?.options?.find((d) => d?.id === value);
      if (v && name?.indexOf(".") > -1) {
        const paths = name.split(".");
        formik.setFieldValue(`${paths[0]}.name`, v?.name);
      }
    }

    if (endpoint === 'resource') {
      GetAppResource(user, resourceEndpoint)
        .then(fetchDataCallback);
    } else if (endpoint === 'allApps') {
      GetAllApps(user.idToken)
        .then(fetchDataCallback);
    }
  }, [
    data,
    filterOptions,
    filteredOptions,
    formik,
    inSubtable,
    keyOption,
    name,
    resourceEndpoint,
    setCurrentSelected,
    setParentStates,
    sortByName,
    user,
    value,
  ]);


  const setDataForNoResourceEndpoint = useCallback(() => {
    const currentValue = single
      ? value
      : dataSet?.filter((opt) => value?.map((val) => val.id).includes(opt.id));
    setData({
      loading: false,
      options: dataSet,
      value: currentValue,
    });

    // Preserve drag and drop order if enabled and we already have an ordered selection
    if (enableDragDrop && orderedSelection.length > 0 && currentValue) {
      // Keep the existing order but update the data from the new dataSet
      const updatedOrderedSelection = orderedSelection.map(item => {
        const updatedItem = dataSet?.find(opt => opt.id === item.id);
        return updatedItem ? { ...item, ...updatedItem } : item;
      });
      setOrderedSelection(updatedOrderedSelection);
    } else if (enableDragDrop && value && Array.isArray(value) && value.length > 0) {
      // For drag and drop, preserve the exact order from formik values
      setOrderedSelection(value);
    } else if (currentValue) {
      setOrderedSelection(currentValue);
    }

    if (typeof setCurrentSelected === "function" && currentValue)
      setCurrentSelected(currentValue);
  }, [dataSet, single, value, enableDragDrop, orderedSelection]);

  useEffect(() => {
    let isMounted = true;

    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }

    if (isMounted) {
      isMounted = false;
      if (resourceEndpoint) {
        fetchData(endpoint);
      } else {
        setDataForNoResourceEndpoint();
      }
    }

    return () => {
      isMounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, dataSet, filterOptions, resourceEndpoint]);

  const customChipStyle = enableDragDrop && !singleSelectOnly && !single ? { 
    chips: {
      display: 'none'
    }
  } : {};

  // Subtopic: FeaturedContent Validation
  const isRequiredFieldObject = (field) => {
    return (
      typeof field === 'object' &&
      field !== null &&
      !Array.isArray(field) &&
      Object.values(field).every(value => value === 'Required')
    );
  }

  const error = formik.errors[name];
  const touched = formik.touched[name];

  return (
    <div className={`flex flex-col w-full ${noMarginBottom ? "" : "mb-5"} data-selector`}>
      <div className="flex items-center space-between w-full">
        {!!label ? (
          <label
            className={`text-lg mb-3 ${withLegend ? "w-1/3" : "w-full"} ${String(labelClassName)}`}
            htmlFor={name}
          >
            {label}
            {required && <span style={{ color: "red" }}> *</span>}
          </label>
        ) : null}
        {withLegend ? (
          <div className="flex items-center justify-end gap-4 w-2/3">
            <div className="flex items-center gap-2 w-fit">
              <div className="w-4 h-4 ttnk-button-color" />
              <span className="w-fit">Regular Directory</span>
            </div>
            <div className="flex items-center gap-2 w-fit">
              <div className="w-4 h-4 bg-132850" />
              <span className="w-fit">Enterprise Directory</span>
            </div>
          </div>
        ) : null}
      </div>
      
      {singleSelectOnly ? (
        <>
          {withSearch ? (
            <>
              <Multiselect
                name={name}
                options={data.options}
                selectedValues={isEmpty(value) ? [] : [value]}
                onSelect={selectHandler}
                displayValue="name"
                selectionLimit={maximumSelection}
                showLimit={maximumSelection}
                placeholder={placeholder || `Select ${label}`}
                hidePlaceholder={isEmpty(placeholder)}
                loading={data.loading}
                loadingMessage={`Loading ${label}...`}
                avoidHighlightFirstOption={true}
                onRemove={removeHandler}
                disable={disabled}
                optionValueDecorator={(val) => {
                  const isEnterprise =
                    data?.options?.find((item) => item?.name === val)
                      ?.isEnterprise ?? false;

                  return (
                    <div
                      style={{
                        padding: "10px 10px",
                      }}
                      className={
                        isEnterprise && withLegend
                          ? "enterprise-option flex flex-row items-center gap-3"
                          : ""
                      }
                    >
                      {isEnterprise && withLegend ? (
                        <div className="flex flex-row items-center enterprise-container">
                          <Work
                            className="h-4 w-4 briefcase-icon"
                            style={{ height: 15, width: 15 }}
                          />
                          <span className="ml-1">{val}</span>
                        </div>
                      ) : (
                        <span className={withLegend ? "ml-6" : ""}>{val}</span>
                      )}
                    </div>
                  );
                }}
                selectedValueDecorator={(val) => {
                  const isEnterprise =
                    data?.options?.find((item) => item?.name === val)
                      ?.isEnterprise || false;
                      const isHidden = !formik.values.enterprise && isEnterprise;
                  return (
                    <div className={isHidden && name === 'directories' ? 'hidden' : isEnterprise ? "enterprise" : ""}>{val}</div>
                  );
                }}
              />

              {touched && error && (
                <div className="text-red-600">
                  {isRequiredFieldObject(error) ? error.id : error}
                </div>
              )}
            </>
          ) : (
            <Select
              name={name}
              formik={formik}
              onChange={onChange}
              disabled={disabled}
              dropdownModifier="-mt-4"
            >
              <option value={null} label={`Select ${label}...`} />
              {data?.options?.map((option) => (
                <option
                  key={option.id}
                  value={option[valueKey || "id"]} // Special case: Need not to fetch the Id
                  label={labelKey ? option[labelKey] : (option.title || option.name)}
                />
              ))}
            </Select>
          )}
        </>
      ) : (
        <>
          <Multiselect
            name={name}
            options={data.options}
            selectedValues={data.value}
            onSelect={selectHandler}
            displayValue="name"
            placeholder={placeholder || `Select ${label ?? ""}`}
            hidePlaceholder={isEmpty(placeholder)}
            loading={data.loading}
            loadingMessage={`Loading ${label ?? ""}...`}
            avoidHighlightFirstOption={true}
            onRemove={removeHandler}
            disable={disabled}
            style={customChipStyle}
            selectionLimit={maximumSelection}
            showLimit={maximumSelection}
            optionValueDecorator={(val) => {
              const isEnterprise =
                data?.options?.find((item) => item?.name === val)
                  ?.isEnterprise || false;

              return (
                <div
                  style={{
                    padding: "10px 10px",
                  }}
                  className={
                    ((isEnterprise && withLegend) || type === "enterprise")
                      ? "enterprise-option flex flex-row items-center gap-3"
                      : ""
                  }
                >
                  {isEnterprise && withLegend ? (
                    <div className="flex flex-row items-center enterprise-container">
                      <Work
                        className="h-4 w-4 briefcase-icon"
                        style={{ height: 15, width: 15 }}
                      />

                      <span className="ml-1">{val}</span>
                    </div>
                  ) : (
                    <span className={withLegend ? "ml-6" : ""}>{val}</span>
                  )}
                </div>
              );
            }}
            selectedValueDecorator={(val) => {
              const isEnterprise =
                data?.options?.find((item) => item?.name === val)
                  ?.isEnterprise || false;
                  const isHidden = !formik.values.enterprise && isEnterprise;
              return (
                <div className={isHidden && name === 'directories' ? 'hidden' : isEnterprise ? "enterprise" : ""}>{val}</div>
              );
            }}
          />
          
          {/* Drag and drop area for selected items - only show if drag and drop is enabled and we're not in single mode */}
          {enableDragDrop && !single && orderedSelection.length > 0 && (
            <div className="mt-2">
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="border border-gray-300 rounded-md p-2 mb-2"
                    >
                      {orderedSelection.map((item, index) => {
                        const isEnterprise = data?.options?.find(
                          (opt) => opt.id === item.id
                        )?.isEnterprise || false;
                        
                        const isHidden = !formik.values.enterprise && isEnterprise && name === 'directories';
                        
                        if (isHidden) {
                          return null;
                        }
                        
                        return (
                          <Draggable
                            key={item.id}
                            draggableId={String(item.id)}
                            index={index}
                            isDragDisabled={disabled}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`${
                                  snapshot.isDragging ? "bg-gray-100" : "bg-white"
                                } flex items-center justify-between p-2 mb-1 rounded border ${
                                  isEnterprise ? "border-blue-500" : "border-gray-300"
                                }`}
                              >
                                <div className="flex items-center">
                                  <span
                                    {...provided.dragHandleProps}
                                    className="cursor-move mr-2"
                                  >
                                    <DragIndicatorIcon style={{ color: '#888' }} />
                                  </span>
                              
                                  <span>
                                    {isEnterprise ? (
                                      <div className="flex items-center">
                                        <Work
                                          className="h-4 w-4 briefcase-icon mr-1"
                                          style={{ height: 15, width: 15 }}
                                        />
                                        {item.name}
                                      </div>
                                    ) : (
                                      item.name
                                    )}
                                  </span>
                                </div>
                                <div className="flex flex-row gap-2 items-center">
                                <span onClick={() => onEditClick(item)} className="w-fit">
                                  <PencilIcon className="h-4 w-4 mr-2 fill-current pencil" />
                                  </span>
                                {!disabled && (
                                  <button
                                    type="button"
                                    onClick={() => removeCustomItem(item)}
                                    className="text-aeblue-default"
                                  >
                                    <IoIosCloseCircle size={18} />
                                  </button>
                                )}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}
          
          {touched && error && (
            <div className="text-red-600">
              {isRequiredFieldObject(error) ? error.id : error}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DataSelector;